import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Notification } from "@rio-cloud/rio-uikit";
import Button from "@rio-cloud/rio-uikit/Button";
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { type SortDirectionType, } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, { type TableViewTogglesViewType, } from "@rio-cloud/rio-uikit/TableViewToggles";
import { ETripStatus, Trip } from "@types";
import { formatDateBr } from "@utils";
import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import omit from "lodash/fp/omit";
import { AuthContext } from "src/providers/AuthProvider";
import ChipStatus from "../ChipStatus";
import { InputDate, PaginatorSize, TableRowLoading } from "../index";
import { ConfigPaginator } from "../PaginatorSize";
import Avatar from "../Avatar/indext";
import TableChip from "../TableChip";

const defaultColumnOrder = [
  "name",
  "vehicle",
  "scheduling",
  "status",
  "tripStatus",
  "origin",
  "destination",
  "stops",
  "distance",
  "duration",
];

type ColumnLabel = {
  [key: string]: string;
};

const columnLabels: ColumnLabel = {
  name: "Motorista",
  vehicle: "Veículo",
  status: "Aceite",
  tripStatus: "Status",
  scheduling: "Agendamentos",
  origin: "Origem",
  destination: "Destino",
  stops: "Paradas",
  distance: "Distância",
  duration: "Duração",
};

type ColumnDetails = {
  [key: string]: number | string;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 270,
    defaultWidth: 270,
    maxWidth: 270,
  },
  scheduling: {
    width: 160,
    defaultWidth: 160,
    maxWidth: 160,
  },
};

const getSortDir = (
  sortDir: SortDirectionType,
  sortBy: string,
  previousSortBy: string
) => {
  if (sortBy === previousSortBy) {
    return sortDir === SortDirection.ASCENDING
      ? SortDirection.DESCENDING
      : SortDirection.ASCENDING;
  }
  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  routes: Trip[];
  searchValue: string;
  usersAmity?: Amity.User[];
  size: number;
  totalSize: number;
  handleSearchValueChange: (text: string) => void;
  handleDownload: () => void;
  handleCreateRoute: () => void;
  handleUpdateRoute: (trip: Trip) => void;
  handlePage: (config: ConfigPaginator) => void;
  handleResetPage: () => void;
  handleSort: (dir: string, newSort: string) => void;
  handleFilter?: () => void;
  handleCancel: (id?: string) => void;
  handleStartTrip: (id: string) => void;
  handleShowTripDetail: (driverId: string, tripId: string, status: string) => void;
  handleShowDrivers?: (trip: Trip) => void;
  handleScheduleAccept: (id: string) => void;
  handleScheduleRefused: (id: string) => void;
  onDateFilterChange?: (start?: string, end?: string) => void;
  handleResetDate?: () => void;
  isLoading: boolean;
};

const TableTrips = (props: Props) => {
  const { getDetailUser } = useContext(AuthContext);
  const { viewType: externalViewType } = props;
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState<SortDirectionType>(
    SortDirection.ASCENDING
  );
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(
    externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(undefined);
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(undefined);

  useEffect(() => {
    if (startDate && endDate) {
      if (startDate.isAfter(endDate)) {
        Notification.error("Data inicial não pode ser maior do que a data final.");
        return;
      }

      const formattedStart = startDate.format("YYYY-MM-DD");
      const formattedEnd = endDate.format("YYYY-MM-DD");
      props.onDateFilterChange?.(formattedStart, formattedEnd);
    }

    if (!startDate && !endDate) {
      props.onDateFilterChange?.(undefined, undefined);
    }
  }, [startDate, endDate]);

  const handleToggleTableSettingsDialog = () =>
    setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) =>
    setViewType(newViewType);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (
    column: string,
    newColumnDetails: ColumnDetails
  ) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir, newSortBy, sortBy));
      props.handleSort(sortDir, newSortBy);
    }
  };

  const handleCardSortChange = (
    newSortBy: string,
    newSortDir: SortDirectionType
  ) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (
    column: string,
    label: string,
    sortByColumn: string,
    sortDirection: SortDirectionType,
  ) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : (
            <SortArrows />
          )}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  // filter data to omit hidden columns
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = props?.routes?.map((vehicle) => ({
    ...withoutHidden(vehicle),
  })) as Trip[];

  // in case a search value is given, filter the data accordingly
  const searchResult = filteredRows;

  // Sort rows according to the sortBy and sortDir settings
  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD &&
    "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS &&
    "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  const renderItems = (row?: Trip) => {
    const enabledEdit = (() => {
      if (!row) return false;

      const isRule1 =
        row.status === ETripStatus.SCHEDULED &&
        row.statusAcceptance === 'PENDING';

      const isRule2 =
        row.status === ETripStatus.SCHEDULED &&
        row.statusAcceptance === 'ACCEPTED';

      const isRule3 =
        (row.status === ETripStatus.FINISHED || row.status === ETripStatus.SYSTEM_FINISHED) &&
        row.statusAcceptance === 'REFUSED';

      return isRule1 || isRule2 || isRule3;
    })();

    const enableDetails = (() => {
      if (!row) return false;

      const isRule1 =
        (row.status === ETripStatus.FINISHED) &&
        row.statusAcceptance === 'ACCEPTED'

      const isRule2 =
        row.status === ETripStatus.INTERRUPTED &&
        row.statusAcceptance === 'ACCEPTED'

      const isRule3 =
        row.status === ETripStatus.MANUALLY_FINISHED_BY_FLEET_MANAGER &&
        row.statusAcceptance === 'ACCEPTED'

      return isRule1 || isRule2 || isRule3;
    })();

    const items = [
      {
        disabled: !(row?.status === ETripStatus.SCHEDULED && row?.statusAcceptance === 'ACCEPTED'),
        value: (
          <>
            <span className="rioglyph rioglyph-drive-history margin-right-10 text-color-primary" />
            <span>Iniciar viagem</span>
          </>
        ),
        onSelect: () => {
          if (row?.id) {
            props?.handleStartTrip(row.id);
          }
        }
      },
      {
        disabled: !enabledEdit,
        value: (
          <>
            <span className="rioglyph rioglyph-pencil-square margin-right-10 text-color-primary" />
            <span>Editar viagem</span>
          </>
        ),
        onSelect: () => {
          if (row?.id) {
            props?.handleUpdateRoute(row);
          }
        }
      },
      {
        disabled: row?.statusAcceptance === 'CANCELED' || row?.status === ETripStatus.SYSTEM_FINISHED,
        value: (
          <>
            <span className="rioglyph rioglyph-trash margin-right-10 text-color-primary" />
            <span>Cancelar viagem</span>
          </>
        ),
        onSelect: () => props?.handleCancel(row?.id)
      },
      {
        disabled: !enableDetails,
        value: (
          <>
            <span className="rioglyph rioglyph-map-location margin-right-10 text-color-primary" />
            <span>Detalhes viagem</span>
          </>
        ),
        onSelect: () => {
          if (row?.id) {
            props.handleShowTripDetail(row.driver, row.id, row.status);
          }
        }
      },
      {
        disabled: !(Array.isArray(row?.dispatchedDrivers) && row!.dispatchedDrivers!.length > 0),
        value: (
          <>
            <span className="rioglyph rioglyph-driver margin-right-10 text-color-primary" />
            <span>Ver motoristas</span>
          </>
        ),
        onSelect: () => {
          if (row?.id) {
            props.handleShowDrivers?.(row);
          }
        }
      },
    ];
    return items;
  };

  const inputProps1: React.HTMLProps<HTMLInputElement> = {
    placeholder: 'Data Inicial',
    id: '1',
    style: { width: 140 }
  };

  const inputProps2: React.HTMLProps<HTMLInputElement> = {
    placeholder: 'Data Final',
    id: '2',
    style: { width: 140 }
  };

  if (!startDate) inputProps1.value = '';
  if (!endDate) inputProps2.value = '';

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);

    if (props?.handleResetDate) {
      props.handleResetDate();
    }
  }

  return (
    <div id="table-trips">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column margin-bottom-0 width-100pct">
              <div className="btn-toolbar table-btn-toolbar ">
                <Button
                  bsStyle={Button.PRIMARY}
                  iconName="rioglyph-plus"
                  onClick={props.handleCreateRoute}
                >
                  Agendar viagem
                </Button>
                <div className="width-400">
                  <TableSearch
                    className="width-100pct"
                    value={props.searchValue}
                    onChange={props.handleSearchValueChange}
                    placeholder="Buscar por motorista, veículo, origem e destino"
                  />
                </div>
                <div className="gap-10 display-flex " style={{ marginTop: -20 }}>
                  <InputDate
                    label=""
                    inputProps={inputProps1}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    value={startDate}
                    onChange={(value, isValid) => {
                      if (!value || value === "") {
                        setStartDate(undefined);
                      } else if (isValid && moment.isMoment(value)) {
                        setStartDate(value);
                      }
                    }}
                  />
                  <InputDate
                    label=""
                    inputProps={inputProps2}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    value={endDate || ''}
                    onChange={(value, isValid) => {
                      if (!value || value === "") {
                        setEndDate(undefined);
                      } else if (isValid && moment.isMoment(value)) {
                        setEndDate(value);
                      }
                    }}
                  />
                  <Button
                    onClick={handleReset}
                    bsStyle={Button.PRIMARY}
                    style={{ marginTop: 20 }}
                    className="rioglyph rioglyph-trash">
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column">
              <TableViewToggles
                initialViewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent="Table view"
                singleCardViewTooltipContent="List view"
                multiCardsViewTooltipContent="Cards view"
              />
            </div>
          </div>
          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title="Table settings"
              onHide={handleToggleTableSettingsDialog}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={defaultColumnOrder}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={columnLabels}
              disabledColumns={[]}
              closeButtonText="Close"
              resetButtonText="Reset to default"
              searchPlaceholder="Search by column name"
              notFoundMessage="No column found for this search value"
              columnsDetails={columnsDetails}
              autoLabel="Auto"
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={(val: string) => console.log(val)}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        {isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState
            headline="Sem resultados"
            message="Por favor refaça a busca."
          />
        )}
        <table className={tableClassNames}>
          <colgroup>
            {columns.map((column) =>
              renderTableCaption(column, columnsDetails[column])
            )}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) =>
                renderTableHead(column, columnLabels[column], sortBy, sortDir)
              )}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
            {props.isLoading ? (
              <TableRowLoading cols={11} rows={10} />
            ) : null}

            {!props.isLoading && isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}


            {!props.isLoading ? rows?.map((row: Trip, index: number) => {
              const proposed = row?.route?.trips?.find((trip) => trip.id === row.id)?.proposedDate?.trim();
              const isValidProposal = !!proposed && row.status === 'PROPOSED';
              const amity_user = getDetailUser?.(row.profileId);
              const hasAssignedDriver = !!row.driverId && !!row.driverName;
              const isMultiDriver = Array.isArray(row.dispatchedDrivers) && row.dispatchedDrivers.length > 0 && !hasAssignedDriver;

              return (
                <tr key={index}>
                  <td key={"Motorista"} data-field={"Motorista"}>
                    {isMultiDriver ? (
                      <div className="display-flex align-items-center justify-content-start">
                        <span
                          className="text-size-14 text-color-primary text-bold"
                          title={row.dispatchedDrivers!.map(d => d.driverName).join(', ')}
                        >
                          <span className="rioglyph rioglyph-driver margin-right-5 text-size-16" />
                          {row.dispatchedDrivers!.length} motoristas nesta viagem
                        </span>
                      </div>
                    ) : (
                      <div className="display-flex flex-direction-row align-items-center justify-content-start">
                        <Avatar url={amity_user?.metadata?.imageProfile?.fileUrl} />
                        <div className="padding-x-15">
                          <span className="text-bold text-size-15 text-capitalize">{row.driverName}</span>
                          <div className="margin-top-2">
                            <TableChip
                              disabledDriver={!row.driverId}
                              disabledProfile={!row.profileId}
                              user={amity_user}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td key={"Veículo"} data-field={"Veículo"}>
                    <span>{row.vehicleName}</span>
                  </td>

                  <td key={"Agendamentos"} data-field={"Agendamentos"}>
                    {isValidProposal ? (
                      <>
                        <div className="display-flex align-items-center margin-bottom-4">
                          <span className="text-thin text-size-14 text-color-rating-2">
                            Sugeriu um novo dia e hora:
                          </span>
                        </div>
                        <div className="display-flex align-items-center">

                          <span className="text-size-12 text-normal text-decoration-line-through text-color-dark">
                            {formatDateBr(row.schedule.date)}
                          </span>
                        </div>
                        <div className="display-flex align-items-center margin-top-2">

                          <span className="text-size-14 text-normal">{formatDateBr(proposed)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="display-flex align-items-center">

                        <span className="text-size-14 text-normal">
                          {formatDateBr(row.schedule.date)}
                        </span>
                      </div>
                    )}
                  </td>

                  <td key={"Aceite"} data-field={"Aceite"}>
                    {isValidProposal ? (
                      <div className="gap-4 display-flex">
                        <Button
                          className="btn-md"
                          bsStyle={Button.PRIMARY}
                          onClick={() => props.handleScheduleAccept(row.id)}
                        >
                          Aceitar
                        </Button>
                        <Button className="btn-md" onClick={() => props.handleScheduleRefused(row.id)}>
                          Recusar
                        </Button>
                      </div>
                    ) : (
                      <ChipStatus status={row?.statusAcceptance} />
                    )}
                  </td>

                  <td key={"Status Viagem"} data-field={"Status Viagem"}>
                    <ChipStatus
                      status={row.status}
                      statusAcceptance={row?.statusAcceptance}
                      manualFinish={row?.manualFinish}
                    />
                  </td>

                  <td key={"Origem"} data-field={"Origem"}>
                    <span>{row.origin}</span>
                  </td>

                  <td key={"Destino"} data-field={"Destino"}>
                    <span>{row.destination}</span>
                  </td>

                  <td key={"Paradas"} data-field={"Paradas"}>
                    <span>{row.stops}</span>
                  </td>

                  <td key={"Distãncia"} data-field={"Distãncia"}>
                    <span>{row.distance?.toLowerCase()?.replace(/\./g, ",")}</span>
                  </td>

                  <td key={"Duração"} data-field={"Duração"}>
                    <span>{row.duration}</span>
                  </td>
                  
                  <td className="table-action">
                    <span>
                      <ButtonDropdown
                        bsSize="md"
                        dropdownClassName="width-200"
                        title={<span className="rioglyph rioglyph-option-vertical" />}
                        variant="link"
                        iconOnly
                        items={renderItems(row)}
                      />
                    </span>
                  </td>
                </tr>
              );
            }) : null}
          </tbody>
        </table>
      </div>
      
      {!props.isLoading ? (
        <PaginatorSize
          size={props.size}
          totalSize={props.totalSize}
          handleLoadMore={props.handlePage}
          handleReset={props.handleResetPage}
        />
      ) : null}
    </div>
  );
};

export default TableTrips;

